import { z } from "zod";

export const catalogStateSchema = z.enum(["Active", "Retired"]);
export const locationTypeSchema = z.enum(["Store", "DistributionCentre"]);
export const unitOfMeasureSchema = z.enum(["Each", "Box", "Pack", "Set"]);
export const demandGroupNameSchema = z.string().trim().min(1);
export const demandGroupStateSchema = z.enum(["Draft", "Active"]);
export const demandQuantitySchema = z
  .string()
  .trim()
  .regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/)
  .refine((value) => /[1-9]/.test(value), "Quantity must be greater than zero");

const catalogRecordBaseSchema = z.object({
  ID: z.string().uuid(),
  name: z.string().trim().min(1),
  state: catalogStateSchema,
  version: z.number().int().positive(),
});

export const locationSchema = catalogRecordBaseSchema.extend({
  kind: z.literal("Location"),
  locationType: locationTypeSchema,
});

export const skuSchema = catalogRecordBaseSchema.extend({
  kind: z.literal("SKU"),
  unitOfMeasure: unitOfMeasureSchema,
  categoryID: z.string().uuid().nullish(),
  erpNo: z.string().nullish(),
  description: z.string().nullish(),
});

export const supplierSchema = catalogRecordBaseSchema.extend({
  kind: z.literal("Supplier"),
});

export const categorySchema = catalogRecordBaseSchema.extend({
  kind: z.literal("Category"),
});

export const catalogRecordSchema = z.discriminatedUnion("kind", [
  locationSchema,
  skuSchema,
  supplierSchema,
  categorySchema,
]);

const projectQuerySchema = z.object({ projectID: z.string().trim().min(1) });
const mutationSchema = projectQuerySchema.extend({
  idempotencyKey: z.string().trim().min(1).max(200),
});
const existingRecordMutationSchema = mutationSchema.extend({
  ID: z.string().uuid(),
  expectedVersion: z.number().int().positive(),
});
const namedSchema = z.object({ name: z.string().trim().min(1).max(240) });
export const demandTargetInputSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("Location"), ID: z.string().uuid() }),
  z.object({ kind: z.literal("Supplier"), ID: z.string().uuid() }),
]);

const demandGroupRequestsSchema = z
  .array(
    z.object({
      skuID: z.string().uuid(),
      quantity: demandQuantitySchema,
    }),
  )
  .min(1)
  .superRefine((requests, context) => {
    const skuIDs = new Set<string>();
    requests.forEach((request, index) => {
      if (skuIDs.has(request.skuID)) {
        context.addIssue({
          code: "custom",
          message: "A SKU can appear only once in a Demand Group",
          path: [index, "skuID"],
        });
      }
      skuIDs.add(request.skuID);
    });
  });

const demandTargetAllocationsSchema = z
  .array(
    z.object({
      target: demandTargetInputSchema,
      quantity: demandQuantitySchema,
    }),
  )
  .superRefine((allocations, context) => {
    const targets = new Set<string>();
    allocations.forEach((allocation, index) => {
      const key = `${allocation.target.kind}:${allocation.target.ID}`;
      if (targets.has(key)) {
        context.addIssue({
          code: "custom",
          message:
            "A Demand Request can have only one allocation per typed target",
          path: [index, "target"],
        });
      }
      targets.add(key);
    });
  });

const demandTargetAssignmentsSchema = z
  .array(
    z.object({
      demandRequestID: z.string().uuid(),
      targetAllocations: demandTargetAllocationsSchema,
    }),
  )
  .min(1)
  .superRefine((assignments, context) => {
    const requestIDs = new Set<string>();
    assignments.forEach((assignment, index) => {
      if (requestIDs.has(assignment.demandRequestID)) {
        context.addIssue({
          code: "custom",
          message: "A Demand Request can be assigned only once per command",
          path: [index, "demandRequestID"],
        });
      }
      requestIDs.add(assignment.demandRequestID);
    });
  });

const receiptLinesSchema = z
  .array(
    z.object({
      allocationID: z.string().uuid(),
      quantity: demandQuantitySchema,
    }),
  )
  .min(1)
  .superRefine((lines, context) => {
    const allocationIDs = new Set<string>();
    lines.forEach((line, index) => {
      if (allocationIDs.has(line.allocationID)) {
        context.addIssue({
          code: "custom",
          message: "An SO/PO line can appear only once in a receipt",
          path: [index, "allocationID"],
        });
      }
      allocationIDs.add(line.allocationID);
    });
  });

const skuDetailsSchema = {
  categoryID: z.string().uuid().nullish(),
  erpNo: z.string().trim().nullish(),
  description: z.string().trim().nullish(),
};

export const rpcMethodSchemas = {
  "catalog.categories.list": projectQuerySchema,
  "catalog.categories.create": mutationSchema.and(namedSchema),
  "catalog.categories.update": existingRecordMutationSchema.and(namedSchema),
  "catalog.categories.retire": existingRecordMutationSchema,
  "catalog.categories.reactivate": existingRecordMutationSchema,
  "catalog.categories.delete": existingRecordMutationSchema,
  "catalog.locations.list": projectQuerySchema.extend({
    locationType: locationTypeSchema.optional(),
  }),
  "catalog.locations.create": mutationSchema
    .extend({ locationType: locationTypeSchema })
    .and(namedSchema),
  "catalog.locations.update": existingRecordMutationSchema
    .extend({ locationType: locationTypeSchema })
    .and(namedSchema),
  "catalog.locations.retire": existingRecordMutationSchema,
  "catalog.locations.reactivate": existingRecordMutationSchema,
  "catalog.locations.delete": existingRecordMutationSchema,
  "catalog.skus.list": projectQuerySchema,
  "catalog.skus.create": mutationSchema
    .extend({ unitOfMeasure: unitOfMeasureSchema, ...skuDetailsSchema })
    .and(namedSchema),
  "catalog.skus.update": existingRecordMutationSchema
    .extend({ unitOfMeasure: unitOfMeasureSchema, ...skuDetailsSchema })
    .and(namedSchema),
  "catalog.skus.retire": existingRecordMutationSchema,
  "catalog.skus.reactivate": existingRecordMutationSchema,
  "catalog.skus.delete": existingRecordMutationSchema,
  "catalog.suppliers.list": projectQuerySchema,
  "catalog.suppliers.create": mutationSchema.and(namedSchema),
  "catalog.suppliers.update": existingRecordMutationSchema.and(namedSchema),
  "catalog.suppliers.retire": existingRecordMutationSchema,
  "catalog.suppliers.reactivate": existingRecordMutationSchema,
  "catalog.suppliers.delete": existingRecordMutationSchema,
  "catalog.audit.list": projectQuerySchema.extend({
    recordID: z.string().uuid().optional(),
  }),
  "catalog.unitOfMeasures.list": projectQuerySchema,
  "demands.list": projectQuerySchema,
  "demandGroups.create": mutationSchema.extend({
    name: demandGroupNameSchema.optional(),
    originLocationID: z.string().uuid(),
    requests: demandGroupRequestsSchema,
  }),
  "demandGroups.update": mutationSchema.extend({
    groupID: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
    name: demandGroupNameSchema,
    requests: z.array(
      z.object({
        demandRequestID: z.string().uuid().optional(),
        skuID: z.string().uuid(),
        quantity: demandQuantitySchema,
      }),
    ),
  }),
  "demandGroups.rename": mutationSchema.extend({
    groupID: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
    name: demandGroupNameSchema,
  }),
  "demandTargetAllocationGroups.list": z.object({
    projectID: z.string().min(1),
  }),
  "demandTargetAllocationGroups.rename": mutationSchema.extend({
    name: z.string().trim().min(1),
    groupID: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
  }),
  "demandTargetAllocationGroups.activate": mutationSchema.extend({
    name: z.string().trim().min(1),
    groupID: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
  }),
  "demandGroups.activate": mutationSchema.extend({
    groupID: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
  }),
  "demandGroups.assignTargets": mutationSchema.extend({
    groupID: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
    assignments: demandTargetAssignmentsSchema,
  }),
  "receiving.list": projectQuerySchema.extend({
    locationID: z.string().uuid(),
  }),
  "receiving.record": mutationSchema.extend({
    allocationGroupID: z.string().uuid(),
    receivedOn: z.iso.date(),
    reference: z.string().trim().max(120).optional(),
    note: z.string().trim().max(2000).optional(),
    lines: receiptLinesSchema,
  }),
  "realtime.issueGrant": z.object({}),
} as const;

export type RpcMethod = keyof typeof rpcMethodSchemas;
export type RpcParams<M extends RpcMethod> = z.infer<
  (typeof rpcMethodSchemas)[M]
>;

export const jsonRpcRequestSchema = z.object({
  jsonrpc: z.literal("2.0"),
  id: z.string().min(1),
  method: z.string(),
  params: z.unknown().optional(),
});

export type JsonRpcRequest = z.infer<typeof jsonRpcRequestSchema>;

export type ChangeSignal = {
  changed: boolean;
  workspaceID?: string;
  projectID?: string;
  aggregateID?: string;
  aggregateVersion?: number;
};

export type JsonRpcSuccess<T> = {
  jsonrpc: "2.0";
  id: string;
  result: T;
};

export type JsonRpcFailure = {
  jsonrpc: "2.0";
  id: string | null;
  error: { code: number; message: string; data?: unknown };
};

const catalogCommandSchema = z.enum([
  "CreateLocation",
  "UpdateLocation",
  "RetireLocation",
  "ReactivateLocation",
  "DeleteLocation",
  "CreateSKU",
  "UpdateSKU",
  "RetireSKU",
  "ReactivateSKU",
  "DeleteSKU",
  "CreateCategory",
  "UpdateCategory",
  "RetireCategory",
  "ReactivateCategory",
  "DeleteCategory",
  "CreateSupplier",
  "UpdateSupplier",
  "RetireSupplier",
  "ReactivateSupplier",
  "DeleteSupplier",
]);

export const catalogAuditFactSchema = z.object({
  ID: z.string().uuid(),
  actorID: z.string().min(1),
  workspaceID: z.string().min(1),
  projectID: z.string().min(1),
  recordKind: z.enum(["Location", "SKU", "Supplier", "Category"]),
  recordID: z.string().uuid(),
  command: catalogCommandSchema,
  at: z.iso.datetime(),
  before: catalogRecordSchema.nullable(),
  after: catalogRecordSchema.nullable(),
});

export const catalogMutationResultSchema = z.object({
  record: catalogRecordSchema,
  deletedID: z.string().uuid().optional(),
  audit: catalogAuditFactSchema,
  change: z.object({
    changed: z.literal(true),
    workspaceID: z.string().min(1),
    projectID: z.string().min(1),
    aggregateID: z.string().uuid(),
    aggregateVersion: z.number().int().positive(),
  }),
});

export const catalogListResultSchema = z.object({
  records: z.array(catalogRecordSchema),
});

export const catalogAuditResultSchema = z.object({
  facts: z.array(catalogAuditFactSchema),
});

export const unitOfMeasuresResultSchema = z.object({
  values: z.array(unitOfMeasureSchema),
});

const demandNamedReferenceSchema = z.object({
  ID: z.string().uuid(),
  name: z.string().trim().min(1),
});

const demandAllocationSchema = z.object({
  target: z.discriminatedUnion("kind", [
    demandNamedReferenceSchema.extend({ kind: z.literal("Location") }),
    demandNamedReferenceSchema.extend({ kind: z.literal("Supplier") }),
  ]),
  quantity: demandQuantitySchema,
});
export const demandRecordSchema = z.object({
  sourceAllocationGroup: demandNamedReferenceSchema
    .extend({ origin: demandNamedReferenceSchema })
    .optional(),
  provenance: z
    .array(
      z.object({
        sourceAllocationID: z.string().uuid(),
        sourceRequestID: z.string().uuid(),
        sourceGroupID: z.string().uuid(),
        sourceGroupName: z.string(),
        quantity: demandQuantitySchema,
      }),
    )
    .optional(),
  ID: z.string().uuid(),
  groupID: z.string().uuid(),
  groupName: demandGroupNameSchema,
  groupState: demandGroupStateSchema,
  groupVersion: z.number().int().positive(),
  origin: demandNamedReferenceSchema,
  sku: demandNamedReferenceSchema.extend({
    unitOfMeasure: unitOfMeasureSchema,
  }),
  quantity: demandQuantitySchema,
  targetAllocations: demandAllocationSchema.array(),
  activeTargetAllocations: demandAllocationSchema.array().optional(),
  createdAt: z.iso.datetime(),
});

export const demandGroupRecordSchema = z.object({
  ID: z.string().uuid(),
  name: demandGroupNameSchema,
  state: demandGroupStateSchema,
  version: z.number().int().positive(),
  origin: demandNamedReferenceSchema,
  createdAt: z.iso.datetime(),
});

const demandActivationSnapshotSchema = z.object({
  group: demandGroupRecordSchema,
  demand: demandRecordSchema,
});

export const demandAuditFactSchema = z.object({
  ID: z.string().uuid(),
  actorID: z.string().min(1),
  workspaceID: z.string().min(1),
  projectID: z.string().min(1),
  demandGroupID: z.string().uuid(),
  demandRequestID: z.string().uuid(),
  command: z.enum([
    "CreateDemandGroup",
    "AssignDemandTargets",
    "RenameDemandGroup",
    "UpdateDemandGroup",
    "ActivateDemandGroup",
  ]),
  at: z.iso.datetime(),
  before: demandActivationSnapshotSchema.optional(),
  after: demandActivationSnapshotSchema.optional(),
});

export const demandGroupMutationResultSchema = z.object({
  group: demandGroupRecordSchema,
  demands: z.array(demandRecordSchema).min(1),
  audits: z.array(demandAuditFactSchema),
  change: z.object({
    changed: z.literal(true),
    workspaceID: z.string().min(1),
    projectID: z.string().min(1),
    aggregateID: z.string().uuid(),
    aggregateVersion: z.number().int().positive(),
  }),
});

export const demandTargetAllocationGroupSchema = z.object({
  incomingGroupID: z.string().uuid().optional(),
  ID: z.string().uuid(),
  name: z.string().trim().min(1).nullable(),
  state: demandGroupStateSchema,
  version: z.number().int().positive(),
  origin: demandNamedReferenceSchema,
  target: demandAllocationSchema.shape.target,
  createdAt: z.iso.datetime(),
  items: z.array(
    z.object({
      ID: z.string().uuid(),
      demandRequestID: z.string().uuid(),
      demandGroupID: z.string().uuid(),
      demandGroupName: demandGroupNameSchema,
      demandGroupState: demandGroupStateSchema,
      sku: demandRecordSchema.shape.sku,
      requiredQuantity: demandQuantitySchema,
      quantity: demandQuantitySchema,
    }),
  ),
});
export const demandTargetAllocationGroupMutationResultSchema = z.object({
  group: demandTargetAllocationGroupSchema,
  audit: z.object({
    ID: z.string().uuid(),
    actorID: z.string().min(1),
    workspaceID: z.string().min(1),
    projectID: z.string().min(1),
    at: z.iso.datetime(),
    before: demandTargetAllocationGroupSchema,
    after: demandTargetAllocationGroupSchema,
  }),
  change: demandGroupMutationResultSchema.shape.change,
});
export const demandTargetAllocationGroupsResultSchema = z.object({
  groups: z.array(demandTargetAllocationGroupSchema),
});
export type DemandTargetAllocationGroup = z.infer<
  typeof demandTargetAllocationGroupSchema
>;
export type DemandTargetAllocationGroupMutationResult = z.infer<
  typeof demandTargetAllocationGroupMutationResultSchema
>;

export const demandListResultSchema = z.object({
  demands: z.array(demandRecordSchema),
});

export const receivingStatusSchema = z.enum([
  "NotReceived",
  "PartiallyReceived",
  "Received",
]);
const receivedQuantitySchema = z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/);
export const receiptSchema = z.object({
  ID: z.string().uuid(),
  allocationGroupID: z.string().uuid(),
  receivedOn: z.iso.date(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  actorID: z.string().min(1),
  recordedAt: z.iso.datetime(),
  lines: z.array(
    z.object({
      ID: z.string().uuid(),
      allocationID: z.string().uuid(),
      sku: demandRecordSchema.shape.sku,
      quantity: demandQuantitySchema,
    }),
  ),
});
export const receivingAllocationGroupSchema = z.object({
  ID: z.string().uuid(),
  name: z.string(),
  origin: demandNamedReferenceSchema,
  target: demandAllocationSchema.shape.target,
  createdAt: z.iso.datetime(),
  status: receivingStatusSchema,
  overReceived: z.boolean(),
  lines: z.array(
    z.object({
      allocationID: z.string().uuid(),
      demandGroupID: z.string().uuid(),
      demandGroupName: demandGroupNameSchema,
      sku: demandRecordSchema.shape.sku,
      orderedQuantity: demandQuantitySchema,
      receivedQuantity: receivedQuantitySchema,
      outstandingQuantity: receivedQuantitySchema,
    }),
  ),
  receipts: z.array(receiptSchema),
});
export const receivingListResultSchema = z.object({
  allocationGroups: z.array(receivingAllocationGroupSchema),
});
export const recordReceiptResultSchema = z.object({
  receipt: receiptSchema,
  allocationGroup: receivingAllocationGroupSchema,
  change: demandGroupMutationResultSchema.shape.change,
});
export type Receipt = z.infer<typeof receiptSchema>;
export type ReceivingAllocationGroup = z.infer<
  typeof receivingAllocationGroupSchema
>;
export type ReceivingListResult = z.infer<typeof receivingListResultSchema>;
export type RecordReceiptResult = z.infer<typeof recordReceiptResultSchema>;

export const realtimeGrantResultSchema = z.object({
  grant: z.string().min(1),
  expiresAt: z.iso.datetime(),
  change: z.object({ changed: z.literal(false) }),
});

export type CatalogRecord = z.infer<typeof catalogRecordSchema>;
export type CatalogMutationResult = z.infer<typeof catalogMutationResultSchema>;
export type CatalogListResult = z.infer<typeof catalogListResultSchema>;
export type CatalogAuditResult = z.infer<typeof catalogAuditResultSchema>;
export type UnitOfMeasuresResult = z.infer<typeof unitOfMeasuresResultSchema>;
export type DemandRecord = z.infer<typeof demandRecordSchema>;
export type DemandGroupRecord = z.infer<typeof demandGroupRecordSchema>;
export type DemandGroupMutationResult = z.infer<
  typeof demandGroupMutationResultSchema
>;
export type DemandListResult = z.infer<typeof demandListResultSchema>;
export type RealtimeGrantResult = z.infer<typeof realtimeGrantResultSchema>;

type LocationListMethod = "catalog.locations.list";
type SKUListMethod = "catalog.skus.list";
type SupplierListMethod = "catalog.suppliers.list";
type AuditMethod = "catalog.audit.list";
type UnitOfMeasureMethod = "catalog.unitOfMeasures.list";
type DemandListMethod = "demands.list";
type DemandMutationMethod =
  | "demandGroups.create"
  | "demandGroups.activate"
  | "demandGroups.assignTargets"
  | "demandGroups.rename"
  | "demandGroups.update";
type RealtimeMethod = "realtime.issueGrant";

export type RpcResultMap = {
  [
    M in Exclude<
      RpcMethod,
      | LocationListMethod
      | SKUListMethod
      | SupplierListMethod
      | "catalog.categories.list"
      | AuditMethod
      | UnitOfMeasureMethod
      | DemandListMethod
      | "demandTargetAllocationGroups.list"
      | "demandTargetAllocationGroups.activate"
      | "demandTargetAllocationGroups.rename"
      | DemandMutationMethod
      | "receiving.list"
      | "receiving.record"
      | RealtimeMethod
    >
  ]: CatalogMutationResult;
} & {
  "catalog.locations.list": CatalogListResult;
  "catalog.skus.list": CatalogListResult;
  "catalog.suppliers.list": CatalogListResult;
  "catalog.categories.list": CatalogListResult;
  "catalog.audit.list": CatalogAuditResult;
  "catalog.unitOfMeasures.list": UnitOfMeasuresResult;
  "demandTargetAllocationGroups.list": z.infer<
    typeof demandTargetAllocationGroupsResultSchema
  >;
  "demandTargetAllocationGroups.activate": DemandTargetAllocationGroupMutationResult;
  "demandTargetAllocationGroups.rename": DemandTargetAllocationGroupMutationResult;
  "demands.list": DemandListResult;
  "demandGroups.rename": DemandGroupMutationResult;
  "demandGroups.update": DemandGroupMutationResult;
  "demandGroups.create": DemandGroupMutationResult;
  "demandGroups.activate": DemandGroupMutationResult;
  "demandGroups.assignTargets": DemandGroupMutationResult;
  "receiving.list": ReceivingListResult;
  "receiving.record": RecordReceiptResult;
  "realtime.issueGrant": RealtimeGrantResult;
};

export type RpcResult<M extends RpcMethod> = RpcResultMap[M];

export const parseRpcResult = <M extends RpcMethod>(
  method: M,
  value: unknown,
): RpcResult<M> => {
  let parsed: unknown;
  if (method === "realtime.issueGrant") {
    parsed = realtimeGrantResultSchema.parse(value);
  } else if (
    method === "demandGroups.rename" ||
    method === "demandGroups.update" ||
    method === "demandGroups.create" ||
    method === "demandGroups.activate" ||
    method === "demandGroups.assignTargets"
  ) {
    parsed = demandGroupMutationResultSchema.parse(value);
  } else if (method === "demandTargetAllocationGroups.list") {
    parsed = demandTargetAllocationGroupsResultSchema.parse(value);
  } else if (
    method === "demandTargetAllocationGroups.activate" ||
    method === "demandTargetAllocationGroups.rename"
  ) {
    parsed = demandTargetAllocationGroupMutationResultSchema.parse(value);
  } else if (method === "demands.list") {
    parsed = demandListResultSchema.parse(value);
  } else if (method === "receiving.list") {
    parsed = receivingListResultSchema.parse(value);
  } else if (method === "receiving.record") {
    parsed = recordReceiptResultSchema.parse(value);
  } else if (method === "catalog.audit.list") {
    parsed = catalogAuditResultSchema.parse(value);
  } else if (method === "catalog.unitOfMeasures.list") {
    parsed = unitOfMeasuresResultSchema.parse(value);
  } else if (method.endsWith(".list")) {
    parsed = catalogListResultSchema.parse(value);
  } else {
    parsed = catalogMutationResultSchema.parse(value);
  }
  return parsed as RpcResult<M>;
};

export const parseRpcParams = <M extends RpcMethod>(
  method: M,
  params: unknown,
): RpcParams<M> => rpcMethodSchemas[method].parse(params ?? {}) as RpcParams<M>;

export const isRpcMethod = (method: string): method is RpcMethod =>
  Object.hasOwn(rpcMethodSchemas, method);
